import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { FileText, Download, Trash2, Upload, Loader2, EyeOff, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_COLORS = {
  quote: "bg-blue-500/15 text-blue-700 border-blue-300",
  invoice: "bg-emerald-500/15 text-emerald-700 border-emerald-300",
  upload: "bg-amber-500/15 text-amber-800 border-amber-300",
  manual: "bg-slate-500/15 text-slate-700 border-slate-300",
};

const BUILTIN_CATEGORIES = ["contract", "notice", "other", "quote", "invoice"];

/**
 * Phase B.5 + Phase F — Client documents panel.
 *
 * Lists every PDF/DOCX that was filed against a client via Send & File or
 * other dispatch flows. Phase F adds a one-click "Upload Document" button
 * for backfilling existing files into the client profile / portal.
 */
export default function ClientDocumentsPanel({ clientId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("contract");
  const [note, setNote] = useState("");
  const [shareToPortal, setShareToPortal] = useState("inherit"); // 'inherit' | 'yes' | 'no'
  const [portalStrategy, setPortalStrategy] = useState("inherit"); // 'inherit' | 'append' | 'replace'
  const [tenantDefaults, setTenantDefaults] = useState(null);
  const [customCategories, setCustomCategories] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/client-documents/client/${clientId}`);
      setDocs(res.data.documents || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/client-documents/client/${clientId}`);
        if (!cancelled) setDocs(res.data.documents || []);
      } catch (e) { /* silent */ }
      try {
        const res = await axios.get(`${API}/portal-settings`);
        if (!cancelled) {
          setTenantDefaults(res.data || null);
          setCustomCategories(res.data?.custom_categories || []);
        }
      } catch (e) { /* admin endpoint — silent if forbidden */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const download = async (doc) => {
    try {
      const res = await axios.get(`${API}/client-documents/${doc.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: doc.mime });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = doc.file_name;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete ${doc.file_name}?`)) return;
    try {
      await axios.delete(`${API}/client-documents/${doc.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const submitUpload = async () => {
    if (!file) {
      toast.error("Pick a file first");
      return;
    }
    setUploadBusy(true);
    const fd = new FormData();
    fd.append("client_id", clientId);
    fd.append("file", file);
    fd.append("category", category);
    if (note) fd.append("note", note);
    if (shareToPortal !== "inherit") fd.append("share_to_portal", shareToPortal === "yes" ? "true" : "false");
    if (portalStrategy !== "inherit") fd.append("portal_strategy", portalStrategy);
    try {
      await axios.post(`${API}/client-documents/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      setUploadOpen(false);
      setFile(null);
      setNote("");
      setShareToPortal("inherit");
      setPortalStrategy("inherit");
      setCategory("contract");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadBusy(false);
    }
  };

  const allCategories = [...new Set([...BUILTIN_CATEGORIES, ...customCategories])];

  const headerBtn = (
    <Button
      size="sm"
      onClick={() => setUploadOpen(true)}
      className="bg-emerald-600 hover:bg-emerald-700"
      data-testid="upload-client-doc-btn"
    >
      <Upload className="h-4 w-4 mr-1" /> Upload Document
    </Button>
  );

  const uploadDialog = (
    <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
      <DialogContent className="max-w-md" data-testid="upload-client-doc-dialog">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>
            Attach an existing file to this client&apos;s profile. Optionally share it through the portal.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>File</Label>
            <Input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="upload-client-doc-file"
            />
            {file && <p className="text-xs text-muted-foreground mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</p>}
          </div>
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-9" data-testid="upload-client-doc-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {allCategories.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`upload-client-doc-cat-${c}`}>
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Internal note — shown alongside the file"
              data-testid="upload-client-doc-note"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Share with portal</Label>
              <Select value={shareToPortal} onValueChange={setShareToPortal}>
                <SelectTrigger className="h-9" data-testid="upload-client-doc-share">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inherit">
                    Inherit ({tenantDefaults?.auto_send_to_portal ? "yes" : "no"})
                  </SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">When sharing</Label>
              <Select value={portalStrategy} onValueChange={setPortalStrategy}>
                <SelectTrigger className="h-9" data-testid="upload-client-doc-strategy">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inherit">
                    Inherit ({tenantDefaults?.doc_strategy || "append"})
                  </SelectItem>
                  <SelectItem value="append">Append</SelectItem>
                  <SelectItem value="replace">Replace</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button
            onClick={submitUpload}
            disabled={uploadBusy || !file}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="upload-client-doc-submit"
          >
            {uploadBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground flex items-center justify-between">
          <span>Loading documents…</span>
          {headerBtn}
        </CardContent>
        {uploadDialog}
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">Filed Documents</p>
        {headerBtn}
      </div>

      {docs.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground" data-testid="client-documents-empty">
            <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
            No documents filed yet. Use the <strong>Upload Document</strong> button above, or <strong>Send &amp; File</strong> on a quote or invoice.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="client-documents-list">
          {docs.map((d) => (
            <Card key={d.id} data-testid={`client-doc-${d.id}`}>
              <CardContent className="p-3 flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                    <FileText className="h-4 w-4 text-emerald-600" />
                    {d.file_name}
                    <Badge variant="outline" className={KIND_COLORS[d.source_kind] || ""}>{d.source_kind}</Badge>
                    {d.category && d.category !== "other" && (
                      <Badge variant="secondary">{d.category}</Badge>
                    )}
                    {d.is_client_visible ? (
                      <Badge variant="outline" className="text-emerald-700 border-emerald-300">
                        <Eye className="h-3 w-3 mr-1" /> Portal
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        <EyeOff className="h-3 w-3 mr-1" /> Private
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {(d.size_bytes / 1024).toFixed(1)} KB · {new Date(d.created_at).toLocaleString()}
                    {d.note ? ` · ${d.note}` : ""}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => download(d)} data-testid={`download-doc-${d.id}`}>
                    <Download className="h-4 w-4 mr-1" /> Download
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(d)} className="text-rose-600" data-testid={`delete-doc-${d.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {uploadDialog}
    </div>
  );
}
